import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Description as NotesIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import ReleaseNotesReaderDialog from '../../components/ReleaseNotesReaderDialog';
import SettingsErrorAlert from './SettingsErrorAlert';

const SERVICE_COLORS = {
  frontend: 'info',
  backend: 'warning',
  database: 'success'
};

const ReleaseNotesSettings = () => {
  const { can } = usePermissions();
  const canView = can('settings', 'view');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [docsBundled, setDocsBundled] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [readerOpen, setReaderOpen] = useState(false);
  const [readerTarget, setReaderTarget] = useState({ service: '', version: '', label: '' });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/settings/release-notes');
      const data = res.data?.data || {};
      setServices(data.services || []);
      setNotes(data.notes || []);
      setDocsBundled(Boolean(data.docs_bundled));
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load release notes';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
  }, [canView]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes.filter((note) => {
      if (serviceFilter !== 'all' && note.service !== serviceFilter) return false;
      if (!q) return true;
      return [
        note.version,
        note.service,
        note.label,
        note.filename,
        note.location
      ].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [notes, serviceFilter, search]);

  const openNote = (note) => {
    setReaderTarget({
      service: note.service,
      version: note.version,
      label: note.label
    });
    setReaderOpen(true);
  };

  if (!canView) {
    return <Alert severity="warning">You do not have permission to view Settings.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <NotesIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Release Notes</Typography>
            <Typography variant="body2" color="text.secondary">
              TenantHub Frontend, Backend, and Database release notes by version.
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Notes are generated on version bump and mirrored under{' '}
        <code>docs/release-notes/&#123;frontend|backend|database&#125;/</code> so the backend image can serve them in-cluster.
        {docsBundled
          ? ' Bundled docs folder detected on this API.'
          : ' Bundled docs folder not detected — rebuild the backend image after a version bump.'}
      </Alert>

      {error && <SettingsErrorAlert message={error} />}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
          placeholder="Version, file, location…"
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Service</InputLabel>
          <Select
            label="Service"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <MenuItem value="all">All services</MenuItem>
            {services.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {filtered.length} note{filtered.length === 1 ? '' : 's'}
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>File name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No release notes found
                </TableCell>
              </TableRow>
            ) : filtered.map((note) => (
              <TableRow key={`${note.service}-${note.version}-${note.location}`} hover>
                <TableCell>
                  <Typography
                    fontWeight={700}
                    sx={{ fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace' }}
                  >
                    {note.version}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={SERVICE_COLORS[note.service] || 'default'}
                    label={note.label || note.service}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace' }}>
                    {note.filename}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                      wordBreak: 'break-all'
                    }}
                  >
                    {note.location || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {note.updated_at ? new Date(note.updated_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell align="right">
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => openNote(note)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 700,
                      border: 0,
                      background: 'none',
                      cursor: 'pointer',
                      color: 'primary.main'
                    }}
                  >
                    View <OpenIcon sx={{ fontSize: 16 }} />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReleaseNotesReaderDialog
        open={readerOpen}
        onClose={() => setReaderOpen(false)}
        service={readerTarget.service}
        version={readerTarget.version}
        label={readerTarget.label}
      />
    </Box>
  );
};

export default ReleaseNotesSettings;
